/**
 * F 232 — the languages the code block offers, and the one Shiki bundle
 * that tokenises all of them.
 *
 * WHY A SUBSET. Stage 1 measured the shipped code block and found NO
 * language picker at all — zero `<select>` elements in the document — so
 * there was no existing list to preserve. The dispatch's premise that
 * "the language picker exists (stock BlockNote)" and quoted a list
 * (Haskell, HCL, HTML, Idris, Java, JavaScript, JSON, Julia, …) does not
 * match what renders. This list is therefore defined HERE, and the picker
 * is built from it, so both stay in one place.
 *
 * WHY THIS EXACT SET. Every id in LANGUAGES must be loadable by the
 * `codeHighlighter` bundle below. Shiki can highlight hundreds of
 * grammars, but a code editor that downloads every one of them to colour
 * a code block is a bad trade; this set covers the languages that appear
 * in the product's own seed content plus the common web/scripting ones.
 */
export interface CodeLanguage {
  /** Shiki grammar id. */
  id: string;
  /** Display name, as the picker shows it. */
  name: string;
  /** Extra ids the picker also accepts. */
  aliases?: string[];
}

export const LANGUAGES: readonly CodeLanguage[] = [
  { id: 'javascript', name: 'JavaScript', aliases: ['js'] },
  { id: 'typescript', name: 'TypeScript', aliases: ['ts'] },
  { id: 'jsx', name: 'JSX' },
  { id: 'tsx', name: 'TSX' },
  { id: 'html', name: 'HTML' },
  { id: 'css', name: 'CSS' },
  { id: 'json', name: 'JSON' },
  { id: 'python', name: 'Python', aliases: ['py'] },
  { id: 'java', name: 'Java' },
  { id: 'bash', name: 'Shell', aliases: ['sh', 'shell'] },
  { id: 'sql', name: 'SQL' },
  { id: 'yaml', name: 'YAML', aliases: ['yml'] },
  { id: 'markdown', name: 'Markdown', aliases: ['md'] },
  { id: 'rust', name: 'Rust', aliases: ['rs'] },
  { id: 'go', name: 'Go' },
  { id: 'diff', name: 'Diff' },
] as const;

/** Every id + alias the picker and the highlighter must agree on. */
export const LANGUAGE_IDS: readonly string[] = LANGUAGES.flatMap((l) => [l.id, ...(l.aliases ?? [])]);

/** The shape `supportedLanguages` expects: a record, not a list. */
export const SUPPORTED_LANGUAGES: Record<string, { name: string; aliases?: string[] }> =
  Object.fromEntries(LANGUAGES.map((l) => [l.id, { name: l.name, ...(l.aliases ? { aliases: l.aliases } : {}) }]));

export const DEFAULT_LANGUAGE = 'javascript';

/** Resolve a picker value or a stored `props.language` to a known id. */
export function resolveLanguage(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_LANGUAGE;
  const lower = raw.toLowerCase();
  return LANGUAGE_IDS.includes(lower) ? lower : DEFAULT_LANGUAGE;
}

/** Human name for a language id, for the menu label. */
export function languageName(id: string): string {
  return LANGUAGES.find((l) => l.id === id)?.name ?? id;
}
